// Member Dashboard
import { useState, useMemo, useRef, useEffect } from 'react';
import { useData } from './DataContext.jsx';
import {
  fmtMin, fmtEuro, fmtDeadline, durationMin,
  startOfWeek, isoFromDate, isoWeekNumber, capitalizeName, currentAbsence,
} from './lib/format.js';
import { computeWeeklyRanking } from './lib/team-ranking.js';
import { weekdayGreeting } from './lib/greetings.js';
import { ProgressRing, ProjectAvatar, BudgetLight, Avatar } from './components.jsx';
import { IconFire, IconClock, IconPlay, IconPlus, IconChevronRight, IconChevronLeft, IconSettings, IconFlag } from './icons.jsx';
import { MemberWorkSettingsModal } from './member-work-settings-modal.jsx';
import { checkinStreak } from './lib/goals.js';

// Lightweight SVG donut. `slices` are { id, color, value, label } and we just
// hand-roll the arc paths instead of pulling in a chart library.
function DonutChart({ slices, size = 220 }) {
  const total = slices.reduce((s, x) => s + x.value, 0);
  if (total === 0) return null;
  const r = size / 2;
  const inner = r * 0.62;
  const cx = r, cy = r;

  // Single slice = full ring
  if (slices.length === 1) {
    return (
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={cx} cy={cy} r={r} fill={slices[0].color}/>
        <circle cx={cx} cy={cy} r={inner} fill="var(--bg-card)"/>
      </svg>
    );
  }

  let acc = 0;
  const wedges = slices.map((s, i) => {
    const startA = (acc / total) * 2 * Math.PI - Math.PI / 2;
    acc += s.value;
    const endA   = (acc / total) * 2 * Math.PI - Math.PI / 2;
    const x1 = cx + r * Math.cos(startA);
    const y1 = cy + r * Math.sin(startA);
    const x2 = cx + r * Math.cos(endA);
    const y2 = cy + r * Math.sin(endA);
    const large = (endA - startA) > Math.PI ? 1 : 0;
    const d = `M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2} Z`;
    return <path key={s.id} d={d} fill={s.color}/>;
  });

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      {wedges}
      <circle cx={cx} cy={cy} r={inner} fill="var(--bg-card)"/>
    </svg>
  );
}

// Single-line greeting box. If the text is wider than the box it scrolls itself
// gently back and forth (marquee); otherwise it just sits there. We measure the
// overflow and only animate when needed.
function GreetingTicker({ text }) {
  const boxRef = useRef(null);
  const textRef = useRef(null);
  const [shift, setShift] = useState(0);

  useEffect(() => {
    const box = boxRef.current, el = textRef.current;
    if (!box || !el) return;
    const measure = () => {
      const overflow = Math.ceil(el.scrollWidth - box.clientWidth);
      setShift(overflow > 2 ? overflow : 0);
    };
    measure();
    // Re-measure on viewport/font changes (serif font loads async).
    const ro = new ResizeObserver(measure);
    ro.observe(box);
    return () => ro.disconnect();
  }, [text]);

  const scrolling = shift > 0;
  // ~36 px/s travel, with a minimum so short overflows aren't too snappy.
  const dur = Math.max(7, (shift + 32) / 36);

  return (
    <div ref={boxRef} className={scrolling ? 'greeting-box scrolling' : 'greeting-box'}>
      <h1
        ref={textRef}
        className={scrolling ? 'marquee' : undefined}
        style={scrolling ? { '--shift': `-${shift + 24}px`, '--dur': `${dur}s` } : undefined}
      >
        {text}
      </h1>
    </div>
  );
}

function Dashboard({ onStartTimer, onOpenNewProject, onNavigate, onOpenCheckin }) {
  const { currentProfile, isAdmin, projects, todaysEntries, projectUsage, projectById, customerById, gamification, dueGoalCheckins, checkins } = useData();
  const [view, setView] = useState('member'); // 'member' | 'team'

  const goalStreak = checkinStreak(checkins, currentProfile?.id);

  // Team-Ansicht is visible to everyone for transparency on the weekly contest;
  // edit buttons inside it stay admin-only.
  const activeView = view;

  const firstName = capitalizeName(currentProfile?.full_name?.split(' ')[0] || 'du');
  const greeting = useMemo(() => weekdayGreeting(firstName), [firstName]);
  const streak = gamification?.streak ?? 0;

  const todayMinutes = todaysEntries.reduce((acc, e) => acc + durationMin(e.from_time, e.to_time), 0);
  const goal = 480;
  const pct = Math.round((todayMinutes / goal) * 100);

  const activeProjects = projects.filter(p => p.status === 'active').slice(0, 4);

  // Aggregate today's entries into pie slices: one slice per (customer, project)
  // tuple, plus separate aggregate slices for meetings and support tasks.
  const pieSlices = useMemo(() => {
    const map = new Map();
    for (const e of todaysEntries) {
      const minutes = durationMin(e.from_time, e.to_time);
      const kind = e.kind || (e.project_id ? 'project' : 'support');
      let key, color, label;
      if (kind === 'project') {
        const proj = projectById(e.project_id);
        const cust = proj ? customerById(proj.customer_id) : null;
        key = `proj:${e.project_id}`;
        color = proj?.color || 'var(--accent)';
        label = `${cust?.name || 'Ohne Kunde'}: ${proj?.title || 'Projekt'}`;
      } else if (kind === 'meeting') {
        const cust = e.customer_id ? customerById(e.customer_id) : null;
        key = `meet:${e.customer_id || 'none'}`;
        color = cust?.color || '#8B5CF6';
        label = cust ? `${cust.name}: Meeting` : 'Meeting';
      } else {
        key = 'supp';
        color = '#5B6172';
        label = 'Begleitaufgabe';
      }
      const slot = map.get(key) || { id: key, color, label, minutes: 0 };
      slot.minutes += minutes;
      map.set(key, slot);
    }
    const slices = [...map.values()].sort((a, b) => b.minutes - a.minutes);
    const total = slices.reduce((s, x) => s + x.minutes, 0);
    return slices.map(s => ({
      ...s,
      value: s.minutes,
      percent: total ? Math.round((s.minutes / total) * 100) : 0,
    }));
  }, [todaysEntries, projectById, customerById]);

  return (
    <>
      <div className="topbar" style={{ paddingRight: 420 }}>
        <GreetingTicker text={greeting} />
        {streak > 0 && (
          <div className="chip chip-accent" style={{ marginLeft: 10, whiteSpace: 'nowrap' }}>
            <IconFire size={13}/> <span className="mono">{streak}</span>-Tage Streak
          </div>
        )}
        <div className="topbar-spacer"/>
        <div className="seg">
          <button className={activeView === 'member' ? 'on' : ''} onClick={() => setView('member')}>Meine Ansicht</button>
          <button className={activeView === 'team' ? 'on' : ''} onClick={() => setView('team')}>Team-Ansicht</button>
        </div>
      </div>

      <div className="content">
        {activeView === 'team' ? <TeamWeekView onNavigate={onNavigate}/> : (
        <>
        {/* Monthly goal check-in nudge */}
        {dueGoalCheckins.length > 0 && (
          <div className="card card-pad" style={{ marginBottom: 20, background: 'var(--accent-soft)', border: '1px solid var(--accent-soft-2)', display: 'flex', alignItems: 'center', gap: 14 }}>
            <span style={{ fontSize: 22 }}>🎯</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--accent)' }}>
                Dein Monats-Check-in steht an
                {dueGoalCheckins.length > 1 ? ` · ${dueGoalCheckins.length} Ziele` : ''}
              </div>
              <div className="tiny" style={{ color: 'var(--ink-2)', marginTop: 2 }}>
                {dueGoalCheckins.map(d => d.customer.name).join(', ')} — reflektiere kurz den Zwischenstand.
              </div>
            </div>
            <button className="btn btn-primary" onClick={onOpenCheckin}>Jetzt eintragen</button>
          </div>
        )}
        {dueGoalCheckins.length === 0 && goalStreak > 0 && (
          <div className="card card-pad" style={{ marginBottom: 20, display: 'flex', alignItems: 'center', gap: 12 }}>
            <IconFlag size={16}/>
            <div className="tiny" style={{ color: 'var(--ink-2)' }}>
              Check-ins erledigt — <b style={{ color: 'var(--ok)' }}>{goalStreak} {goalStreak === 1 ? 'Monat' : 'Monate'}</b> in Folge reflektiert. 🔥
            </div>
          </div>
        )}

        {/* Hero — full width */}
        <div className="card card-pad-lg" style={{ position: 'relative', overflow: 'hidden', marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 28 }}>
            <ProgressRing pct={pct} size={148} stroke={11}>
              <div style={{ textAlign: 'center' }}>
                <div className="serif" style={{ fontSize: 30, fontWeight: 500, lineHeight: 1 }}>{fmtMin(todayMinutes)}</div>
                <div className="tiny muted" style={{ marginTop: 6 }}>von {fmtMin(goal)} heute</div>
              </div>
            </ProgressRing>
            <div style={{ flex: 1 }}>
              <div className="eyebrow">Heute</div>
              <div className="serif" style={{ fontSize: 26, fontWeight: 500, marginTop: 6, lineHeight: 1.2, letterSpacing: '-0.01em' }}>
                {todayMinutes === 0
                  ? "Noch nichts erfasst. Leg los."
                  : todayMinutes >= goal
                  ? "Tagesziel erreicht. Stark."
                  : `Schön dabei. ${fmtMin(goal - todayMinutes)} bis zum Tagesziel.`}
              </div>
              <div className="muted" style={{ marginTop: 8, fontSize: 13 }}>
                {todaysEntries.length === 0
                  ? 'Keine Einträge heute'
                  : `${todaysEntries.length} ${todaysEntries.length === 1 ? 'Eintrag' : 'Einträge'} heute`}
              </div>
              <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
                <button className="btn btn-primary" onClick={onStartTimer}>
                  <IconPlay size={13}/> Zeit erfassen
                </button>
                <button className="btn" onClick={() => onNavigate({ view: 'times' })}>
                  <IconClock size={13}/> Tag ansehen
                </button>
              </div>
            </div>
          </div>
          <div style={{
            position: 'absolute', right: -60, top: -60, width: 260, height: 260,
            background: 'radial-gradient(closest-side, rgba(255,107,74,0.18), transparent)',
            pointerEvents: 'none',
          }}/>
        </div>

        {/* Active projects (swapped above Heute) */}
        <div className="card card-pad-lg" style={{ marginBottom: 20 }}>
          <div className="card-title">
            <span>Offene Projekte</span>
            <button className="btn btn-ghost" style={{ padding: '4px 10px' }} onClick={() => onNavigate({ view: 'projects' })}>
              Alle ansehen <IconChevronRight size={13}/>
            </button>
          </div>
          {activeProjects.length === 0 ? (
            <div className="tiny muted" style={{ padding: '12px 4px' }}>
              Keine aktiven Projekte. Lege unter „Projekte" eines an.
            </div>
          ) : (
            <div className="grid grid-2" style={{ gap: 12 }}>
              {activeProjects.map(p => {
                const usage = projectUsage[p.id] || { minutes: 0, euro: 0 };
                const pct = p.budget_minutes ? Math.round((usage.minutes / p.budget_minutes) * 100) : 0;
                const cust = customerById(p.customer_id);
                return (
                  <div key={p.id} onClick={() => onNavigate({ view: 'project', id: p.id })} style={{
                    padding: 16, borderRadius: 12,
                    background: 'var(--bg-sunken)', cursor: 'pointer',
                    border: '1px solid var(--stroke)',
                    transition: 'background 150ms, border-color 150ms',
                  }} onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-hover)'}
                     onMouseLeave={e => e.currentTarget.style.background = 'var(--bg-sunken)'}>
                    <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                      <ProjectAvatar project={p} size={36}/>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13.5, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.title}</div>
                        <div className="tiny muted" style={{ marginTop: 2 }}>
                          {cust?.name || '—'}{p.deadline ? ` · Deadline ${fmtDeadline(p.deadline)}` : ''}
                        </div>
                      </div>
                      <BudgetLight pct={pct}/>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 14, gap: 10 }}>
                      <div style={{ flex: 1 }}>
                        <div className="bar"><span style={{ width: `${Math.min(100,pct)}%`, background: pct < 70 ? 'var(--ok)' : pct < 90 ? 'var(--warn)' : 'var(--crit)' }}/></div>
                      </div>
                      <div className="mono tiny" style={{ color: 'var(--ink-2)' }}>{pct}%</div>
                    </div>
                    {p.budget_minutes && (
                      <div className="tiny muted" style={{ marginTop: 8 }}>
                        {fmtMin(usage.minutes)} von {fmtMin(p.budget_minutes)}
                        {p.budget_euro ? ` · ${fmtEuro(usage.euro)} / ${fmtEuro(p.budget_euro)}` : ''}
                        {p.budget_is_estimate && <span style={{ color: 'var(--warn)', fontWeight: 600 }}> · geschätzt</span>}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Heute — donut + legend */}
        <div className="card card-pad-lg">
          <div className="card-title">
            <span>Heute</span>
            <span className="sub mono">{fmtMin(todayMinutes)} erfasst</span>
          </div>
          {pieSlices.length === 0 ? (
            <div className="tiny muted" style={{ padding: '16px 4px' }}>
              Noch keine Zeiteinträge heute. Öffne „Meine Zeiten", um welche anzulegen.
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 32 }}>
              <div style={{ position: 'relative', flex: '0 0 220px' }}>
                <DonutChart slices={pieSlices} size={220}/>
                <div style={{
                  position: 'absolute', inset: 0, display: 'grid', placeItems: 'center',
                  pointerEvents: 'none',
                }}>
                  <div style={{ textAlign: 'center' }}>
                    <div className="serif" style={{ fontSize: 26, fontWeight: 500, lineHeight: 1 }}>{fmtMin(todayMinutes)}</div>
                    <div className="tiny muted" style={{ marginTop: 4 }}>heute erfasst</div>
                  </div>
                </div>
              </div>
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
                {pieSlices.map(s => (
                  <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ width: 12, height: 12, borderRadius: 3, background: s.color, flex: '0 0 12px' }}/>
                    <div style={{ flex: 1, minWidth: 0, fontSize: 13, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {s.label}
                    </div>
                    <div className="mono tiny" style={{ color: 'var(--ink-2)' }}>{fmtMin(s.minutes)}</div>
                    <div className="mono tiny muted" style={{ width: 38, textAlign: 'right' }}>{s.percent}%</div>
                  </div>
                ))}
              </div>
            </div>
          )}
          <button className="btn btn-ghost" style={{ justifyContent: 'center', marginTop: 16, width: '100%' }} onClick={() => onNavigate({ view: 'times' })}>
            <IconPlus size={13}/> Eintrag hinzufügen
          </button>
        </div>
        </>
        )}
      </div>
    </>
  );
}

// --- Team-Ansicht: weekly per-member summary ---------------------------------
function TeamWeekView({ onNavigate }) {
  const { profiles, timeEntries, absences, tasks, projects, involvedByProject, projectById, isAdmin } = useData();
  const [editProfile, setEditProfile] = useState(null);
  // Which bonus chip is expanded to show its underlying items. Keyed by
  // `${profileId}:${type}` so only one chip is open at a time.
  const [openChip, setOpenChip] = useState(null);
  // 0 = current week, -1 = last week, +1 = next week, …
  const [weekOffset, setWeekOffset] = useState(0);

  const weekStart = useMemo(() => {
    const base = new Date();
    base.setDate(base.getDate() + weekOffset * 7);
    return startOfWeek(base);
  }, [weekOffset]);
  const weekEnd = useMemo(() => {
    const e = new Date(weekStart);
    e.setDate(e.getDate() + 6);
    return e;
  }, [weekStart]);

  const startIso = useMemo(() => isoFromDate(weekStart), [weekStart]);
  const endIso   = useMemo(() => isoFromDate(weekEnd),   [weekEnd]);

  const weekEntries = useMemo(
    () => timeEntries.filter(te => te.entry_date >= startIso && te.entry_date <= endIso),
    [timeEntries, startIso, endIso]
  );

  // Ranking by tracking percentage + bonuses (tasks, deadlines)
  const ranking = useMemo(
    () => computeWeeklyRanking(profiles, weekEntries, weekStart, {
      absences, tasks, projects, involvedByProject,
    }),
    [profiles, weekEntries, weekStart, absences, tasks, projects, involvedByProject]
  );

  const perUser = useMemo(() => {
    const dayLabels = ['Mo','Di','Mi','Do','Fr','Sa','So'];
    return ranking.map(({ profile: p, totalMinutes: total, lateMinutes, expectedMinutes, absentDays, percent, tasksDone, projectsOnTime, projectsLate, descriptionPoints, projectsCreated, doneTasks, onTimeProjects, lateProjects, createdProjects, tasksBonus, projectsOnTimeBonus, projectsLateBonus, descriptionBonus, bonusPoints, scaleFactor, score, rank }) => {
      const userEntries = weekEntries.filter(te => te.user_id === p.id);

      const projMap = new Map();
      for (const e of userEntries) {
        const pr = projectById(e.project_id);
        const m = projMap.get(e.project_id) || { project: pr, minutes: 0 };
        m.minutes += durationMin(e.from_time, e.to_time);
        projMap.set(e.project_id, m);
      }
      const byProject = [...projMap.values()].sort((a, b) => b.minutes - a.minutes);

      const byDay = dayLabels.map((label, i) => {
        const date = new Date(weekStart);
        date.setDate(date.getDate() + i);
        const iso = isoFromDate(date);
        const minutes = userEntries
          .filter(e => e.entry_date === iso)
          .reduce((s, e) => s + durationMin(e.from_time, e.to_time), 0);
        return { label, date, minutes, isWeekend: i >= 5 };
      });

      return { profile: p, total, lateMinutes, expectedMinutes, absentDays, percent, tasksDone, projectsOnTime, projectsLate, descriptionPoints, projectsCreated, doneTasks, onTimeProjects, lateProjects, createdProjects, tasksBonus, projectsOnTimeBonus, projectsLateBonus, descriptionBonus, bonusPoints, scaleFactor, score, rank, byProject, byDay };
    });
  }, [ranking, weekEntries, projectById, weekStart]);

  const teamTotal = perUser.reduce((s, u) => s + u.total, 0);
  const maxDayMin = Math.max(60, ...perUser.flatMap(u => u.byDay.map(d => d.minutes)));
  const weekLabel = `KW ${isoWeekNumber(weekStart)} · ${weekStart.toLocaleDateString('de-DE', { day: '2-digit', month: 'short' })} – ${weekEnd.toLocaleDateString('de-DE', { day: '2-digit', month: 'short' })}`;
  const weekRelLabel =
    weekOffset === 0 ? 'diese Woche'
    : weekOffset === -1 ? 'letzte Woche'
    : weekOffset === 1 ? 'nächste Woche'
    : weekOffset < 0 ? `vor ${-weekOffset} Wochen`
    : `in ${weekOffset} Wochen`;

  function rankBadge(rank, hasData) {
    if (!hasData) return <span className="tiny muted mono" style={{ width: 32, textAlign: 'center' }}>—</span>;
    if (rank === 1) return <span style={{ fontSize: 22, lineHeight: 1, width: 32, textAlign: 'center' }}>🥇</span>;
    if (rank === 2) return <span style={{ fontSize: 22, lineHeight: 1, width: 32, textAlign: 'center' }}>🥈</span>;
    if (rank === 3) return <span style={{ fontSize: 22, lineHeight: 1, width: 32, textAlign: 'center' }}>🥉</span>;
    return <span className="mono" style={{ width: 32, textAlign: 'center', color: 'var(--ink-3)', fontSize: 13 }}>#{rank}</span>;
  }

  return (
    <div className="card card-pad-lg">
      <div className="card-title">
        <span>Team-Ansicht · {weekRelLabel}</span>
        <span className="sub" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <button className="icon-btn" onClick={() => setWeekOffset(o => o - 1)} title="Vorherige Woche">
            <IconChevronLeft size={14}/>
          </button>
          <span style={{ minWidth: 200, textAlign: 'center' }}>{weekLabel} · {fmtMin(teamTotal)} gesamt</span>
          <button className="icon-btn" onClick={() => setWeekOffset(o => o + 1)} title="Nächste Woche">
            <IconChevronRight size={14}/>
          </button>
          {weekOffset !== 0 && (
            <button className="btn btn-ghost" style={{ padding: '2px 8px', marginLeft: 4 }}
                    onClick={() => setWeekOffset(0)} title="Zurück zu dieser Woche">
              Heute
            </button>
          )}
        </span>
      </div>

      {profiles.length === 0 ? (
        <div className="tiny muted">Noch keine Team-Mitglieder.</div>
      ) : perUser.map(({ profile, total, lateMinutes, expectedMinutes, absentDays, percent, tasksDone, projectsOnTime, projectsLate, descriptionPoints, projectsCreated, doneTasks, onTimeProjects, lateProjects, createdProjects, tasksBonus, projectsOnTimeBonus, projectsLateBonus, descriptionBonus, bonusPoints, scaleFactor, score, rank, byProject, byDay }) => {
        const abs = currentAbsence(profile.id, absences);
        const badge = abs ? {
          emoji: abs.kind === 'sick' ? '🤒' : '🏖',
          title: abs.kind === 'sick' ? 'Krank' : 'Urlaub',
        } : null;
        // Bonus chips that can be expanded to reveal the items behind the count.
        const chipDefs = [
          { key: 'tasks',   show: tasksDone > 0,        cls: 'chip',            label: `✓ ${tasksDone} ${tasksDone === 1 ? 'Aufgabe' : 'Aufgaben'} · +${tasksBonus} P`,       items: doneTasks,      kind: 'task' },
          { key: 'onTime',  show: projectsOnTime > 0,   cls: 'chip chip-ok',    label: `📅 ${projectsOnTime} pünktlich · +${projectsOnTimeBonus} P`,                            items: onTimeProjects, kind: 'project' },
          { key: 'late',    show: projectsLate > 0,     cls: 'chip chip-warn',  label: `⏰ ${projectsLate} überfällig · +${projectsLateBonus} P`,                               items: lateProjects,   kind: 'project' },
          { key: 'created', show: descriptionPoints > 0, cls: 'chip chip-accent', label: `📝 ${projectsCreated} angelegt · +${descriptionBonus} P`,                            items: createdProjects, kind: 'project' },
        ];
        const openDef = chipDefs.find(c => c.show && `${profile.id}:${c.key}` === openChip);
        return (
        <div key={profile.id} style={{ padding: '16px 0', borderTop: '1px solid var(--stroke)', opacity: abs ? 0.6 : 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {rankBadge(rank, score > 0)}
            <Avatar user={profile} size={32} badge={badge}/>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}>
                {capitalizeName(profile.full_name) || profile.email}
                {absentDays > 0 && (
                  <span className="chip" style={{ height: 20, fontSize: 11 }}>
                    {absentDays} {absentDays === 1 ? 'Tag' : 'Tage'} abwesend
                  </span>
                )}
              </div>
              <div className="tiny muted">{profile.role === 'admin' ? 'Admin' : 'Member'}</div>
            </div>
            {isAdmin && (
              <button className="icon-btn" onClick={() => setEditProfile(profile)}
                      title="Arbeitszeit & Abwesenheiten">
                <IconSettings size={14}/>
              </button>
            )}
            <div style={{ textAlign: 'right', minWidth: 120 }}>
              <div className="serif" style={{ fontSize: 22, fontWeight: 500, color: score > 0 ? (percent >= 100 ? 'var(--ok)' : 'var(--ink)') : 'var(--ink-3)' }}>
                {score} <span className="tiny muted" style={{ fontFamily: 'var(--sans)', fontWeight: 500 }}>P</span>
              </div>
              <div className="tiny muted mono">
                {expectedMinutes > 0 ? `${percent}%` : '—'}
                {bonusPoints > 0 && <> + {bonusPoints} Bonus</>}
              </div>
              <div className="tiny muted mono" style={{ marginTop: 2 }}>
                {fmtMin(total)} / {fmtMin(expectedMinutes)}
              </div>
            </div>
          </div>

          {(bonusPoints > 0 || lateMinutes > 0) && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8, marginLeft: 44, alignItems: 'center' }}>
              {chipDefs.filter(c => c.show).map(c => {
                const id = `${profile.id}:${c.key}`;
                const isOpen = id === openChip;
                return (
                  <button
                    key={c.key}
                    className={c.cls}
                    style={{ height: 22, cursor: 'pointer', font: 'inherit',
                             boxShadow: isOpen ? '0 0 0 2px var(--stroke) inset' : 'none' }}
                    onClick={() => setOpenChip(o => (o === id ? null : id))}
                    title="Zum Anzeigen anklicken"
                  >
                    {c.label} {isOpen ? '▴' : '▾'}
                  </button>
                );
              })}
              {lateMinutes > 0 && (
                <span className="chip" style={{ height: 22, color: 'var(--ink-3)' }}
                      title="Einträge, die nicht am selben Tag erfasst wurden, fließen nicht in den Score">
                  ⏱️ {fmtMin(lateMinutes)} nachgetragen · 0 P
                </span>
              )}
              {scaleFactor > 1.001 && (
                <span className="tiny muted" title="Bonus-Punkte werden im Verhältnis 40h-Woche zu deinem Wochensoll skaliert">
                  · ×{scaleFactor.toFixed(1)} (Teilzeit/Abwesenheit)
                </span>
              )}
            </div>
          )}

          {openDef && (
            <div style={{ marginTop: 8, marginLeft: 44, display: 'flex', flexDirection: 'column', gap: 4, maxWidth: 420 }}>
              {openDef.items.length === 0 ? (
                <div className="tiny muted">Keine Details verfügbar.</div>
              ) : openDef.items.map(item => {
                const projId = openDef.kind === 'task' ? item.project_id : item.id;
                const proj = projId ? projectById(projId) : null;
                const label = openDef.kind === 'task'
                  ? (item.title || 'Aufgabe')
                  : (item.title || 'Ohne Projekt');
                const late = openDef.kind === 'project' && item.deadline && item.completed_at
                  ? item.completed_at.slice(0, 10) > item.deadline
                  : false;
                return (
                  <button
                    key={item.id}
                    onClick={() => projId && onNavigate?.({ view: 'project', id: projId })}
                    disabled={!projId}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 8, textAlign: 'left',
                      padding: '6px 10px', borderRadius: 8, border: '1px solid var(--stroke)',
                      background: 'var(--bg-sunken)', cursor: projId ? 'pointer' : 'default', width: '100%',
                    }}
                    title={projId ? 'Projekt öffnen' : undefined}
                  >
                    {proj && <ProjectAvatar project={proj} size={20}/>}
                    <span style={{ fontSize: 13, color: 'var(--ink)', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {label}
                    </span>
                    {openDef.kind === 'task' && proj && (
                      <span className="tiny muted" style={{ whiteSpace: 'nowrap' }}>{proj.title}</span>
                    )}
                    {openDef.kind === 'project' && item.deadline && (
                      <span className="tiny mono" style={{ color: late ? 'var(--warn)' : 'var(--ink-3)', whiteSpace: 'nowrap' }}>
                        {fmtDeadline(item.deadline)}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}

          {/* Mini week chart Mo–So */}
          <div style={{ display: 'flex', gap: 6, alignItems: 'flex-end', height: 50, marginTop: 14, marginLeft: 44, maxWidth: 360 }}>
            {byDay.map((d, i) => {
              const h = d.minutes ? Math.max(4, (d.minutes / maxDayMin) * 38) : 3;
              return (
                <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <div style={{
                    width: '100%',
                    height: h,
                    background: d.minutes > 0
                      ? (d.isWeekend ? 'var(--ink-3)' : 'var(--accent)')
                      : 'var(--bg-hover)',
                    borderRadius: 4,
                    opacity: d.isWeekend && !d.minutes ? 0.4 : 1,
                    transition: 'height 300ms ease',
                  }} title={fmtMin(d.minutes)}/>
                  <div className="tiny muted" style={{ fontSize: 10, marginTop: 4 }}>
                    {d.label}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Project chips */}
          {byProject.length > 0 ? (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 12, marginLeft: 44 }}>
              {byProject.slice(0, 5).map(({ project, minutes }) => (
                <div key={project?.id || 'unknown'} style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  padding: '4px 10px 4px 4px',
                  background: 'var(--bg-sunken)', border: '1px solid var(--stroke)',
                  borderRadius: 999,
                }}>
                  {project && <ProjectAvatar project={project} size={20}/>}
                  <span style={{ fontSize: 12, color: 'var(--ink-2)' }}>{project?.title || 'Ohne Projekt'}</span>
                  <span className="mono tiny muted">{fmtMin(minutes)}</span>
                </div>
              ))}
              {byProject.length > 5 && (
                <div className="tiny muted" style={{ alignSelf: 'center' }}>
                  +{byProject.length - 5} weitere
                </div>
              )}
            </div>
          ) : (
            <div className="tiny muted" style={{ marginTop: 10, marginLeft: 44 }}>
              Noch nichts erfasst diese Woche.
            </div>
          )}
        </div>
        );
      })}

      {editProfile && (
        <MemberWorkSettingsModal
          profile={editProfile}
          onClose={() => setEditProfile(null)}
        />
      )}
    </div>
  );
}

export { Dashboard };
